import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  CreditCard,
  MessageCircle,
  User as UserIcon,
  Wallet,
  XCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminUserService, adminWalletService, getApiErrorMessage } from '@/services';
import type { AdminDetailUser } from '@/types/admin';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { formatDateForDisplay, formatDateTime } from '@/utils/dates';
import { notify } from '@/utils/notify';

function formatNaira(amount: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
  }).format(amount);
}

function StatusBadge({ status }: { status: AdminDetailUser['status'] }) {
  const styles = {
    active: 'bg-secondary/15 text-secondary',
    blocked: 'bg-red-500/15 text-red-500',
    pending: 'bg-amber-500/15 text-amber-600',
  };
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize', styles[status])}>
      {status}
    </span>
  );
}

function TypeBadge({ userType }: { userType: AdminDetailUser['userType'] }) {
  return (
    <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold capitalize text-primary">
      {userType}
    </span>
  );
}

function VaStatusBadge({ status }: { status: 'active' | 'inactive' | 'pending' }) {
  const styles = {
    active: 'bg-secondary/15 text-secondary',
    inactive: 'bg-red-500/15 text-red-500',
    pending: 'bg-amber-500/15 text-amber-600',
  };
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize', styles[status])}>
      {status}
    </span>
  );
}

function InfoField({
  label,
  value,
  mutedClass,
  valueClass,
}: {
  label: string;
  value: ReactNode;
  mutedClass: string;
  valueClass: string;
}) {
  return (
    <div>
      <p className={cn('text-xs font-medium', mutedClass)}>{label}</p>
      <div className={cn('mt-1 text-sm font-medium', valueClass)}>{value}</div>
    </div>
  );
}

function BoolIndicator({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle className="h-5 w-5 text-secondary" aria-label="Yes" />
  ) : (
    <XCircle className="h-5 w-5 text-red-500" aria-label="No" />
  );
}

function openWhatsApp(phone: string, firstName: string) {
  let formatted = phone.replace(/[\s\-()]/g, '');
  if (formatted.startsWith('0')) formatted = `234${formatted.slice(1)}`;
  else if (formatted.startsWith('+234')) formatted = formatted.slice(1);
  else if (!formatted.startsWith('234')) formatted = `234${formatted}`;

  const message = encodeURIComponent(
    `Hi ${firstName}! Your AS DataLink account is active. Fund your wallet to enjoy airtime, data, bills, and verification services.`,
  );
  window.open(`https://wa.me/${formatted}?text=${message}`, '_blank', 'noopener,noreferrer');
}

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [user, setUser] = useState<AdminDetailUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [fundAmount, setFundAmount] = useState('');
  const [fundNote, setFundNote] = useState('');
  const [funding, setFunding] = useState(false);

  const loadUser = useCallback(async () => {
    const userId = Number(id);
    if (!Number.isInteger(userId) || userId < 1) {
      notify.error('Invalid user ID');
      navigate(ROUTES.adminUsers);
      return;
    }

    setLoading(true);
    try {
      const res = await adminUserService.getUserById(userId);
      setUser(res.data?.user ?? null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load user'));
      navigate(ROUTES.adminUsers);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  async function handleManualFund(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    const amount = Number(fundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      notify.error('Enter a valid amount');
      return;
    }

    setFunding(true);
    try {
      const res = await adminWalletService.manualFundUser(user.id, amount, fundNote.trim() || undefined);
      if (res.data?.user) {
        setUser((prev) => (prev ? { ...prev, walletBalance: res.data!.user!.walletBalance } : prev));
      } else {
        await loadUser();
      }
      setFundAmount('');
      setFundNote('');
      notify.success(res.message || 'Wallet funded');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to fund wallet'));
    } finally {
      setFunding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading user details" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className={cn('py-24 text-center text-sm', shell.empty)}>
        User not found.{' '}
        <Link to={ROUTES.adminUsers} className="text-primary hover:underline">
          Back to users
        </Link>
      </div>
    );
  }

  const fullName = `${user.firstName} ${user.lastName}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => navigate(ROUTES.adminUsers)}
            className={cn('rounded-xl p-2', shell.ghostBtn)}
            aria-label="Back to users"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <UserIcon className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold sm:text-2xl">{fullName}</h1>
            <p className={cn('mt-0.5 truncate text-sm', shell.cardMuted)}>{user.email ?? user.phone}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <TypeBadge userType={user.userType} />
              <StatusBadge status={user.status} />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => openWhatsApp(user.phone, user.firstName)}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-secondary/10 px-4 py-2.5 text-sm font-semibold text-secondary transition-colors hover:bg-secondary/20"
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <h2 className="mb-4 text-base font-semibold">Profile Information</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoField label="First Name" value={user.firstName} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Last Name" value={user.lastName} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Email" value={user.email ?? '—'} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Phone" value={user.phone} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="State" value={user.state ?? 'Not specified'} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField
                label="Gender"
                value={user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Not specified'}
                mutedClass={shell.cardMuted}
                valueClass={shell.tableCell}
              />
              <InfoField label="Date of Birth" value={formatDateForDisplay(user.dateOfBirth)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="User Type" value={<TypeBadge userType={user.userType} />} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
            </div>
          </Card>

          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <h2 className="mb-4 text-base font-semibold">Account Status</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoField label="Account Status" value={<StatusBadge status={user.status} />} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Transaction PIN" value={<BoolIndicator ok={user.pinEnabled} />} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Last Login" value={formatDateTime(user.lastLoginAt)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Last Logout" value={formatDateTime(user.lastLogoutAt)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Registered" value={formatDateTime(user.createdAt)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="Last Updated" value={formatDateTime(user.updatedAt)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
            </div>
          </Card>

          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <h2 className="mb-4 text-base font-semibold">Verification</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoField label="NIN" value={user.nin ?? 'Not provided'} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
              <InfoField label="BVN" value={user.bvn ?? 'Not provided'} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
            </div>
          </Card>

          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Virtual Accounts</h2>
              <span className={cn('text-xs', shell.cardMuted)}>
                {user.virtualAccounts.length} account{user.virtualAccounts.length !== 1 ? 's' : ''}
              </span>
            </div>

            {user.virtualAccounts.length === 0 ? (
              <div className={cn('rounded-xl border border-dashed px-4 py-8 text-center text-sm', shell.tableWrap, shell.empty)}>
                No virtual account yet. Generation will be available when the wallet module is live.
              </div>
            ) : (
              <div className="space-y-4">
                {user.virtualAccounts.map((va) => (
                  <div key={va.id} className={cn('rounded-xl border p-4', shell.tableWrap)}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoField label="Account Name" value={va.accountName} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
                      <InfoField
                        label="Account Number"
                        value={<span className="font-mono">{va.accountNumber}</span>}
                        mutedClass={shell.cardMuted}
                        valueClass={shell.tableCell}
                      />
                      <InfoField label="Bank" value={va.bankName} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
                      <InfoField label="Provider" value={va.provider} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
                      <InfoField label="Status" value={<VaStatusBadge status={va.status} />} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
                      <InfoField label="Created" value={formatDateTime(va.createdAt)} mutedClass={shell.cardMuted} valueClass={shell.tableCell} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <div className="mb-3 flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              <h2 className="text-base font-semibold">Wallet Balance</h2>
            </div>
            <p className="text-2xl font-bold sm:text-3xl">{formatNaira(user.walletBalance)}</p>

            <form onSubmit={(e) => void handleManualFund(e)} className="mt-4 space-y-3 border-t pt-4">
              <p className={cn('text-xs font-semibold uppercase tracking-wide', shell.cardMuted)}>Manual funding</p>
              <input
                type="number"
                min="1"
                step="0.01"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                placeholder="Amount (₦)"
                className={cn('w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2', shell.input)}
              />
              <textarea
                value={fundNote}
                onChange={(e) => setFundNote(e.target.value)}
                placeholder="Note (optional)"
                rows={2}
                className={cn('w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2', shell.input)}
              />
              <button
                type="submit"
                disabled={funding}
                className="w-full rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
              >
                {funding ? 'Funding...' : 'Credit wallet'}
              </button>
            </form>
          </Card>

          <Card className={cn('p-4 sm:p-6', shell.card)}>
            <div className="mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-secondary" />
              <h2 className="text-base font-semibold">Quick Summary</h2>
            </div>
            <dl className={cn('space-y-3 text-sm', shell.tableCell)}>
              <div className="flex items-center justify-between gap-3">
                <dt className={shell.cardMuted}>User ID</dt>
                <dd className="font-semibold">#{user.id}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className={shell.cardMuted}>PIN set</dt>
                <dd>{user.pinEnabled ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className={shell.cardMuted}>Virtual accounts</dt>
                <dd>{user.virtualAccounts.length}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className={shell.cardMuted}>NIN on file</dt>
                <dd>{user.nin ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className={shell.cardMuted}>BVN on file</dt>
                <dd>{user.bvn ? 'Yes' : 'No'}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}
